import React, { FC, useCallback, useEffect } from 'react'
import { Grid, Theme, Typography } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { endOfDay, startOfDay } from 'date-fns'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import { ReadBusinessEntitiesRequestDto, IBusinessEntities } from '@sparling/financial-common'
import { useHistory } from 'react-router-dom'
import { DataPicker } from '../../../components/DatePicker'
import { Search } from '../../../components/Search'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { icons } from '../../../constants/icons'
import { IDataItem, TableComponent } from '../../../components/Table'
import { columns } from './businessEntitiesList'
import { getBusinessEntitiesRequest, resetBusinessEntitiesFilters } from '../../../store/actions'
import { businessEntitiesLoading, getBusinessEntitiesData } from '../../../store/selectors'
import { routes } from '../../../constants/routes'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(4),
    '& p': {
      display: 'none',
    },
  },
  rightSide: {
    flexWrap: 'nowrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: 'max-content',
    paddingTop: theme.spacing(2),
    '& > div:first-child': {
      marginLeft: 0,
    },
  },
  rightSideInput: {
    width: 135,
    height: 40,
    marginRight: 6,
    marginLeft: 6,
    '& .MuiGrid-container': {
      width: 'auto',
      '& .MuiFormControl-root': {
        width: 'auto',
      },
    },
  },
  title: {
    paddingTop: theme.spacing(2),
  },
  table: {
    paddingTop: theme.spacing(3),
  },
}))

export const BusinessEntities: FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useDispatch()

  const entitiesIsLoading = useSelector(businessEntitiesLoading)
  const { items, meta } = useSelector(getBusinessEntitiesData)

  const formMethods = useForm({
    mode: 'onBlur',
  })

  const { watch, getValues } = formMethods

  useEffect(() => {
    dispatch(resetBusinessEntitiesFilters())
  }, [dispatch])

  useEffect(() => {
    const subscription = watch(() => {
      dispatch(getBusinessEntitiesRequest(getValues() as ReadBusinessEntitiesRequestDto))
    })
    return () => subscription.unsubscribe()
  }, [dispatch, watch, getValues])

  const loadEntitiesData = useCallback(
    (page = 1) => {
      dispatch(getBusinessEntitiesRequest({ page }))
    },
    [dispatch],
  )

  const getEntityId = useCallback(
    (id) => {
      history.push(routes.SETTINGS_ENTITY_DETAILS.replace(':id', id))
    },
    [history],
  )

  useEffect(() => {
    history.push('?page=1')
  }, [history, loadEntitiesData])

  useEffect(() => {
    loadEntitiesData()
  }, [loadEntitiesData])

  return (
    <>
      <Grid container className={classes.topWrap}>
        <Typography variant="h1" className={classes.title}>
          Business Entities
        </Typography>
        <FormProvider {...formMethods}>
          <Grid container className={classes.rightSide}>
            <DataPicker
              placeholder={'Start Date'}
              margin={'0 8px'}
              dataPickerName={'startDate'}
              required={false}
              maxDate={getValues().endDate}
              formatValue={startOfDay}
            />
            <DataPicker
              placeholder={'End Date'}
              margin={'0 8px'}
              dataPickerName={'endDate'}
              required={false}
              minDate={getValues().startDate}
              formatValue={endOfDay}
            />

            <Search inputName={'search'} />

            <GreenButton width={120} prefix={icons.ADD} ml={8} text={'Create'} href={routes.SETTINGS_ENTITY_CREATE} />
          </Grid>
        </FormProvider>
        <Grid container className={classes.table}>
          <TableComponent
            getElementId={getEntityId}
            columns={columns}
            data={items as IDataItem<IBusinessEntities>[]}
            params={meta}
            loadData={loadEntitiesData}
            loading={entitiesIsLoading}
            isShowInfoIcon={false}
            isShowEditIcon={false}
          />
        </Grid>
      </Grid>
    </>
  )
}
